import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  deleteARShapeApi,
  getAllARShapesApi,
  getARShapeByIdApi,
  postARShapeApi,
  updateARShapeApi,
  uploadARShapeFileApi,
} from "@/redux/api/ar-shapes-api/ARShapesApi";

export const getAllARShapes = createAsyncThunk(
  "arShapes/getAllARShapes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAllARShapesApi();
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.response?.data?.message,
      });
    }
  },
);

export const postARShape = createAsyncThunk(
  "arShapes/postARShape",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await postARShapeApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.response?.data?.message,
      });
    }
  },
);

export const getARShapeById = createAsyncThunk(
  "arShapes/getARShapeById",
  async (arShapeId, { rejectWithValue }) => {
    try {
      const response = await getARShapeByIdApi(arShapeId);
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.response?.data?.message,
      });
    }
  },
);

export const updateARShape = createAsyncThunk(
  "arShapes/updateARShape",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateARShapeApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.response?.data?.message,
      });
    }
  },
);

export const deleteARShape = createAsyncThunk(
  "arShapes/deleteARShape",
  async (arShapeId, { rejectWithValue }) => {
    try {
      const response = await deleteARShapeApi(arShapeId);
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.response?.data?.message,
      });
    }
  },
);

export const uploadARShapeFile = createAsyncThunk(
  "arShapes/uploadARShapeFile",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await uploadARShapeFileApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.response?.data?.message,
      });
    }
  },
);

const initialState = {
  loading: false,
  saving: false,
  deleting: false,
  uploading: false,
  arShapes: [],
  selectedARShape: {},
  uploadResponse: {},
  error: null,
};

const ARShapesSlice = createSlice({
  name: "arShapes",
  initialState,
  reducers: {
    clearSelectedARShape: (state) => {
      state.selectedARShape = {};
    },
    clearARShapeUploadResponse: (state) => {
      state.uploadResponse = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllARShapes.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.arShapes = [];
      })
      .addCase(getAllARShapes.fulfilled, (state, action) => {
        state.loading = false;
        state.arShapes = action.payload?.response || action.payload || [];
      })
      .addCase(getAllARShapes.rejected, (state, action) => {
        state.loading = false;
        state.arShapes = [];
        state.error = action?.payload?.errorMessage || action.error.message;
      });

    builder
      .addCase(postARShape.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(postARShape.fulfilled, (state, action) => {
        state.saving = false;
        state.selectedARShape = action.payload?.response || action.payload || {};
      })
      .addCase(postARShape.rejected, (state, action) => {
        state.saving = false;
        state.error = action?.payload?.errorMessage || action.error.message;
      });

    builder
      .addCase(getARShapeById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedARShape = {};
      })
      .addCase(getARShapeById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedARShape =
          action.payload?.response || action.payload || {};
      })
      .addCase(getARShapeById.rejected, (state, action) => {
        state.loading = false;
        state.selectedARShape = {};
        state.error = action?.payload?.errorMessage || action.error.message;
      });

    builder
      .addCase(updateARShape.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateARShape.fulfilled, (state, action) => {
        state.saving = false;
        state.selectedARShape = action.payload?.response || action.payload || {};
      })
      .addCase(updateARShape.rejected, (state, action) => {
        state.saving = false;
        state.error = action?.payload?.errorMessage || action.error.message;
      });

    builder
      .addCase(deleteARShape.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteARShape.fulfilled, (state, action) => {
        state.deleting = false;
        state.selectedARShape = action.payload?.response || action.payload || {};
      })
      .addCase(deleteARShape.rejected, (state, action) => {
        state.deleting = false;
        state.error = action?.payload?.errorMessage || action.error.message;
      });

    builder
      .addCase(uploadARShapeFile.pending, (state) => {
        state.uploading = true;
        state.error = null;
        state.uploadResponse = {};
      })
      .addCase(uploadARShapeFile.fulfilled, (state, action) => {
        state.uploading = false;
        state.uploadResponse = action.payload?.response || action.payload || {};
      })
      .addCase(uploadARShapeFile.rejected, (state, action) => {
        state.uploading = false;
        state.uploadResponse = {};
        state.error = action?.payload?.errorMessage || action.error.message;
      });
  },
});

export const { clearSelectedARShape, clearARShapeUploadResponse } =
  ARShapesSlice.actions;

export default ARShapesSlice.reducer;
